// Terms of nouns: gender, number, case, declension (Kielioppi § 2, 02-nouns.mdx; lessons 5–25).
// HV facts as 02-nouns.mdx and lesson 8 state them (wiki: High_Valyrian_Grammar, Noun_Declensions).
import { defineTerms } from './define';

export const terms = defineTerms({
  substantiivi: {
    fi: {
      name: 'substantiivi',
      short: '**Substantiivi** eli nimisana nimeää olennon, asian tai paikan, kuten *mies*, *talo* tai *Valyria*.',
      compare: 'Sama sanaluokka kuin suomessa; valyriassa jokainen substantiivi kuuluu lisäksi johonkin sukuun.',
    },
    en: {
      name: 'noun',
      short: 'A **noun** names a being, a thing or a place, such as *man*, *house* or *Valyria*.',
      compare: 'The same word class as in Finnish; in High Valyrian every noun also belongs to a gender.',
    },
    related: ['suku', 'sija', 'luku'],
    firstIn: 'genders',
  },
  suku: {
    fi: {
      name: 'suku',
      short: '**Suku** on substantiivien kieliopillinen ryhmä, jonka mukaan siihen liittyvät sanat, kuten adjektiivit ja pronominit, saavat muotonsa.',
      compare: 'Suomessa sukuja ei ole lainkaan, ei edes eri *hän*-sanaa naiselle ja miehelle; kyse ei myöskään ole sukulaisuudesta.',
      long: 'Valyriassa sukuja on neljä: kuusuku, aurinkosuku, maasuku ja vesisuku. Suku ei ole sukupuoli: sekä ’mies’ että ’nainen’ ovat kuusukua. Sanan loppu ja merkitys antavat vihjeitä, mutta poikkeuksia on paljon, joten suku opetellaan jokaisen sanan mukana.',
    },
    en: {
      name: 'gender',
      short: '**Gender** is a grammatical class of nouns that decides the form of the words that go with them, such as adjectives and pronouns.',
      compare: 'Finnish has no genders at all, not even separate words for ‘he’ and ‘she’.',
      long: 'High Valyrian has four: lunar, solar, terrestrial and aquatic. Gender is not sex: ‘man’ and ‘woman’ are both lunar. A word’s ending and meaning give hints, but there are many exceptions, so learn the gender with each word.',
    },
    related: ['kuusuku', 'aurinkosuku', 'maasuku', 'vesisuku', 'mukautuminen'],
    firstIn: 'genders',
  },
  kuusuku: {
    fi: {
      name: 'kuusuku',
      short: '**Kuusuku** on yksi valyrian neljästä suvusta, nimetty tyyppisanansa [[hūra]] ’kuu’ mukaan.',
      compare: 'Suomessa ei ole vastinetta.',
      long: 'Kuusukuinen sana päättyy useimmiten vokaaliin. Kuusuvussa on monia ihmisiä tarkoittavia sanoja (’mies’, ’nainen’), yöeläimiä ja sotavarusteita, mutta nämä ovat muistiapuja, eivät sääntöjä.',
    },
    en: {
      name: 'lunar gender',
      short: 'The **lunar gender** is one of High Valyrian’s four genders, named after its type word [[hūra]] ‘moon’.',
      compare: 'Finnish has nothing like it.',
      long: 'Lunar nouns most often end in a vowel. It has many words for people (‘man’, ‘woman’), nocturnal animals and war gear, but these are memory aids, not rules.',
    },
    related: ['suku', 'aurinkosuku', 'maasuku', 'vesisuku'],
    firstIn: 'genders',
  },
  aurinkosuku: {
    fi: {
      name: 'aurinkosuku',
      short: '**Aurinkosuku** on yksi valyrian neljästä suvusta, nimetty tyyppisanansa [[vēzos]] ’aurinko’ mukaan.',
      compare: 'Suomessa ei ole vastinetta.',
      long: 'Aurinkosukuinen sana päättyy useimmiten *s*:ään tai *z*:aan, ja sen monikossa on usein kaksois-*s*. Aurinkosuvussa on ammatteja, päiväeläimiä ja ruumiinosia.',
    },
    en: {
      name: 'solar gender',
      short: 'The **solar gender** is one of High Valyrian’s four genders, named after its type word [[vēzos]] ‘sun’.',
      compare: 'Finnish has nothing like it.',
      long: 'Solar nouns most often end in *s* or *z*, and their plural often has a double *s*. It has professions, diurnal animals and body parts.',
    },
    related: ['suku', 'kuusuku', 'maasuku', 'vesisuku'],
    firstIn: 'genders',
  },
  maasuku: {
    fi: {
      name: 'maasuku',
      short: '**Maasuku** on yksi valyrian neljästä suvusta, nimetty tyyppisanansa [[tegon]] ’maa’ mukaan.',
      compare: 'Suomessa ei ole vastinetta.',
      long: 'Maasukuinen sana päättyy useimmiten *n*:ään. Maasuvussa on paikkoja, esineitä, abstrakteja asioita, ruokia, kasveja ja metalleja.',
    },
    en: {
      name: 'terrestrial gender',
      short: 'The **terrestrial gender** is one of High Valyrian’s four genders, named after its type word [[tegon]] ‘earth’.',
      compare: 'Finnish has nothing like it.',
      long: 'Terrestrial nouns most often end in *n*. It has places, objects, abstractions, foods, plants and metals.',
    },
    related: ['suku', 'kuusuku', 'aurinkosuku', 'vesisuku'],
    firstIn: 'genders',
  },
  vesisuku: {
    fi: {
      name: 'vesisuku',
      short: '**Vesisuku** on yksi valyrian neljästä suvusta, nimetty tyyppisanansa [[embar]] ’meri’ mukaan.',
      compare: 'Suomessa ei ole vastinetta.',
      long: 'Vesisukuinen sana päättyy useimmiten *r*:ään, ja *r* säilyy lähes kaikissa sen muodoissa. Vesisuvussa on nesteitä ja vesistöjä.',
    },
    en: {
      name: 'aquatic gender',
      short: 'The **aquatic gender** is one of High Valyrian’s four genders, named after its type word [[embar]] ‘sea’.',
      compare: 'Finnish has nothing like it.',
      long: 'Aquatic nouns most often end in *r*, and the *r* stays in nearly every form. It has liquids and bodies of water.',
    },
    related: ['suku', 'kuusuku', 'aurinkosuku', 'maasuku'],
    firstIn: 'genders',
  },
  luku: {
    fi: {
      name: 'luku',
      short: '**Luku** kertoo, kuinka monesta on kyse; valyriassa lukuja on neljä: yksikkö, monikko, muutama ja kollektiivi.',
      compare: 'Suomessa lukuja on kaksi, yksikkö ja monikko.',
      long: 'Valyrian luvut asettuvat kahden kysymyksen ristiin: yksi vai useampi, tarkka vai epätarkka. Verbillä on silti vain yksikkö ja monikko.',
    },
    en: {
      name: 'number',
      short: '**Number** tells how many are meant; High Valyrian has four: singular, plural, paucal and collective.',
      compare: 'Finnish has two, singular and plural.',
      long: 'The four numbers sit at the crossing of two questions: one or more, exact or inexact. Verbs still have only singular and plural.',
    },
    related: ['yksikko', 'monikko', 'muutama', 'kollektiivi'],
    firstIn: 'numbers',
  },
  yksikko: {
    fi: {
      name: 'yksikkö',
      short: '**Yksikkö** tarkoittaa täsmälleen yhtä.',
      compare: 'Kuten suomen *mies*.',
    },
    en: {
      name: 'singular',
      short: 'The **singular** means exactly one.',
      compare: 'As in Finnish *mies*.',
    },
    related: ['luku', 'monikko'],
    firstIn: 'numbers',
  },
  monikko: {
    fi: {
      name: 'monikko',
      short: '**Monikko** tarkoittaa monta, epätarkasti: ehkä kahta, ehkä kahtakymmentä.',
      compare: 'Lähes kuin suomen monikko, mutta valyriassa sen rinnalla on kaksi muuta lukua, muutama ja kollektiivi.',
    },
    en: {
      name: 'plural',
      short: 'The **plural** means many, inexactly: maybe two, maybe twenty.',
      compare: 'Much like the Finnish plural, but High Valyrian has two more numbers beside it, the paucal and the collective.',
    },
    related: ['luku', 'muutama', 'kollektiivi'],
    firstIn: 'numbers',
  },
  muutama: {
    fi: {
      name: 'muutama',
      short: '**Muutama** eli paukaali on luku, joka tarkoittaa pientä joukkoa, jonka kokoa ei tarkkaan tiedetä: ’muutama, joitakin’.',
      compare: 'Suomessa tätä lukua ei ole; partitiivi (*joitakin päärynöitä*) voi tuntua samalta, mutta se on sija eikä luku.',
      long: 'Muutaman tunnistaa *-n*:stä ([[-un]], [[-yn]], [[-ȳn]], [[-in]]). Se saa verbin, pronominin ja adjektiivin monikkoon, vaikka suomen *muutama mies laulaa* on yksikössä.',
    },
    en: {
      name: 'paucal',
      short: 'The **paucal** is a number for a small group whose exact size is not known: ‘a few, some’.',
      compare: 'Finnish has no such number; the partitive (*joitakin päärynöitä*) may feel similar, but it is a case, not a number.',
      long: 'The paucal is marked by *-n* ([[-un]], [[-yn]], [[-ȳn]], [[-in]]). It takes a plural verb, pronoun and adjective, although Finnish *muutama mies laulaa* is singular.',
    },
    related: ['luku', 'kollektiivi', 'monikko', 'partitiivi'],
    firstIn: 'numbers',
  },
  kollektiivi: {
    fi: {
      name: 'kollektiivi',
      short: '**Kollektiivi** on luku, joka tarkoittaa koko joukkoa, kaikkia yhtenä kokonaisuutena.',
      compare: 'Suomen *väki* ja *vaatetus* ovat lähellä: ne ovat yksikössä, ja niin valyrian kollektiivikin saa verbin yksikköön.',
      long: 'Kollektiivin tunnistaa *-r*:stä. Lauseen [[valar morghūlis]] ensimmäinen sana, ’kaikki miehet’, on kollektiivi, ja Petersonin mukaan koko neljän luvun järjestelmä syntyi siitä.',
    },
    en: {
      name: 'collective',
      short: 'The **collective** is a number for the whole group, all of them as one.',
      compare: 'Finnish *väki* ‘folk’ is close: it is singular, and a High Valyrian collective takes a singular verb too.',
      long: 'The collective is marked by *-r*. The first word of [[valar morghūlis]], ‘all men’, is a collective, and Peterson says the whole four-number system grew out of it.',
    },
    related: ['luku', 'muutama', 'yksikko'],
    firstIn: 'numbers',
  },
  sija: {
    fi: {
      name: 'sija',
      short: '**Sija** on sanan taivutusmuoto, joka kertoo sen tehtävän lauseessa: kuka tekee, mitä, kenelle, missä.',
      compare: 'Suomessa sijoja on viisitoista, valyriassa kahdeksan, joten yksi valyrian sija vastaa usein kahta tai kolmea suomen sijaa.',
      long: 'Koska sijapääte kertoo tehtävän, sanajärjestys voi joustaa. Valyrian sijat ovat nominatiivi, akkusatiivi, genetiivi, datiivi, lokatiivi, instrumentaali, komitatiivi ja vokatiivi.',
    },
    en: {
      name: 'case',
      short: 'A **case** is an inflected form of a word that shows its role in the sentence: who acts, on what, to whom, where.',
      compare: 'Finnish has fifteen cases, High Valyrian eight, so one High Valyrian case often covers two or three Finnish ones.',
      long: 'Since the case ending tells the role, word order can flex. The High Valyrian cases are the nominative, accusative, genitive, dative, locative, instrumental, comitative and vocative.',
    },
    related: ['nominatiivi', 'akkusatiivi', 'genetiivi', 'datiivi', 'lokatiivi', 'instrumentaali', 'komitatiivi', 'vokatiivi'],
    firstIn: 'cases',
  },
  nominatiivi: {
    fi: {
      name: 'nominatiivi',
      short: '**Nominatiivi** on subjektin ja sanakirjamuodon sija: kuka? mikä?',
      compare: 'Kuten suomen nominatiivi (*mies*).',
      long: 'Myös predikatiivi on valyriassa nominatiivissa, siinäkin, missä suomi käyttää partitiivia (*ovat pieniä*). Predikatiivi kertoo olla-lauseessa, mikä tai millainen subjekti on.',
    },
    en: {
      name: 'nominative',
      short: 'The **nominative** is the case of the subject and of the dictionary form: who? what?',
      compare: 'As the Finnish nominative (*mies*).',
      long: 'A predicate complement (what the subject is, in a clause with ‘be’) is in the nominative too, even where Finnish uses the partitive (*ovat pieniä*).',
    },
    related: ['sija', 'subjekti', 'predikatiivi'],
    firstIn: 'cases',
  },
  akkusatiivi: {
    fi: {
      name: 'akkusatiivi',
      short: '**Akkusatiivi** on objektin sija: kenet? minkä?',
      compare:
        'Lähin suomessa on kokonaisobjekti (*näen naisen*); valyriassa partitiiviobjektia ei ole, vaan objekti on akkusatiivissa myös siellä, missä suomi sanoisi *luin kirjaa* tai *en näe miestä*.',
    },
    en: {
      name: 'accusative',
      short: 'The **accusative** is the case of the object: whom? what?',
      compare:
        'The nearest in Finnish is the total object (*näen naisen*); there is no partitive object, so the object is accusative even where Finnish says *luin kirjaa* or *en näe miestä*.',
    },
    related: ['sija', 'objekti', 'partitiivi', 'kokonaisobjekti'],
    firstIn: 'cases',
  },
  genetiivi: {
    fi: {
      name: 'genetiivi',
      short: '**Genetiivi** on omistajan sija: kenen? minkä?',
      compare: 'Kuten suomen *miehen*, mutta suomen *-n* merkitsee myös objektia, valyrian genetiivi ei koskaan.',
      long: 'Genetiivi on myös monen postposition edellä. Sen päälle rakentuvat muut sijat: datiivi on lähes aina genetiivi + *-t*, ja instrumentaali ja komitatiivi genetiivi + *s* tai *m* + teemavokaali.',
    },
    en: {
      name: 'genitive',
      short: 'The **genitive** is the case of the owner: whose?',
      compare: 'Like Finnish *miehen*, but Finnish *-n* also marks objects; the High Valyrian genitive never does.',
      long: 'The genitive also stands before many postpositions. Other cases build on it: the dative is nearly always the genitive + *-t*, the instrumental and comitative the genitive + *s* or *m* + the theme vowel.',
    },
    related: ['sija', 'datiivi', 'postpositio'],
    firstIn: 'cases',
  },
  datiivi: {
    fi: {
      name: 'datiivi',
      short: '**Datiivi** on vastaanottajan ja määränpään sija: kenelle? minne?',
      compare: 'Se vastaa suomen allatiivia ja illatiivia (*pojalle*, *taloon*); ’sisään’ ja ’ulos’ ilmaistaan prepositioilla.',
      long: 'Datiivi on lähes aina genetiivi + *-t*. Monikossa genetiivi ja datiivi ovat aina samat.',
    },
    en: {
      name: 'dative',
      short: 'The **dative** is the case of the recipient and the destination: to whom? where to?',
      compare: 'It covers Finnish allative and illative (*pojalle*, *taloon*); ‘into’ and ‘out of’ take prepositions.',
      long: 'The dative is nearly always the genitive + *-t*. In the plural the genitive and dative are always the same.',
    },
    related: ['sija', 'genetiivi', 'allatiivi', 'illatiivi', 'epasuora-objekti'],
    firstIn: 'cases',
  },
  lokatiivi: {
    fi: {
      name: 'lokatiivi',
      short: '**Lokatiivi** on paikan ja ajan sija: missä? milloin?',
      compare: 'Se vastaa sekä suomen inessiiviä että adessiivia (*Valyriassa*, *pöydällä*); asiayhteys ratkaisee, kumpaa tarkoitetaan.',
      long: 'Lokatiivi on pidentynyt teemavokaali tai sama kuin datiivi. Preposition [[hae]] kanssa se tarkoittaa ’kuin, jonakin’, kuten suomen essiivi.',
    },
    en: {
      name: 'locative',
      short: 'The **locative** is the case of place and time: where? when?',
      compare: 'It covers both Finnish inessive and adessive (*Valyriassa*, *pöydällä*); context decides which is meant.',
      long: 'The locative is a lengthened theme vowel or the same as the dative. With the preposition [[hae]] it means ‘like, as’, like the Finnish essive.',
    },
    related: ['sija', 'inessiivi', 'adessiivi', 'essiivi', 'teemavokaali'],
    firstIn: 'cases',
  },
  instrumentaali: {
    fi: {
      name: 'instrumentaali',
      short: '**Instrumentaali** on välineen ja keinon sija: millä?',
      compare: 'Lähin suomessa on adessiivi välineenä (*veitsellä*); seuralainen, *ystävän kanssa*, on valyriassa eri sija, komitatiivi.',
      long: 'Moni verbi vaatii täydennyksen instrumentaalissa, esimerkiksi nimi nimettäessä. Myös vertailun kohde on instrumentaalissa, kuten suomen *sinun lohikäärmettäsi suurempi*.',
    },
    en: {
      name: 'instrumental',
      short: 'The **instrumental** is the case of the tool and the means: with what?',
      compare: 'The nearest in Finnish is the adessive of means (*veitsellä*); a companion, ‘with a friend’, is another case, the comitative.',
      long: 'Many verbs govern the instrumental, for example the name when naming. The standard of comparison is in the instrumental too, as in Finnish *sinun lohikäärmettäsi suurempi*.',
    },
    related: ['sija', 'komitatiivi', 'adessiivi', 'valenssi'],
    firstIn: 'cases',
  },
  komitatiivi: {
    fi: {
      name: 'komitatiivi',
      short: '**Komitatiivi** on seuran sija: kenen kanssa?',
      compare: 'Suomessakin on komitatiivi (*ystävineen*), mutta se on harvinainen; valyriassa se on arkinen sija, jolla on yksikkö ja monikko.',
      long: 'Instrumentaali kertoo elottoman välineen, komitatiivi elollisen seuralaisen. Joissakin taivutusluokissa nämä kaksi sijaa ovat samat.',
    },
    en: {
      name: 'comitative',
      short: 'The **comitative** is the case of company: with whom?',
      compare: 'Finnish has a comitative too (*ystävineen*), but it is rare; in High Valyrian it is an everyday case with a singular and a plural.',
      long: 'The instrumental marks an inanimate tool, the comitative a living companion. In some declensions the two cases are the same.',
    },
    related: ['sija', 'instrumentaali'],
    firstIn: 'cases',
  },
  vokatiivi: {
    fi: {
      name: 'vokatiivi',
      short: '**Vokatiivi** on puhuttelun sija: sillä kutsutaan tai puhutellaan jotakuta.',
      compare: 'Suomessa ei ole vastinetta: puhuttelemme nominatiivilla (*Mies!*).',
      long: 'Vokatiivissa on yksikössä ja monikossa aina *s*. Kollektiivissa sen tilalla on *z*. Vokatiivi ja infinitiivi yhdessä ovat käsky jollekulle muulle kuin puhuteltavalle: ’pysähtyköön poika!’',
    },
    en: {
      name: 'vocative',
      short: 'The **vocative** is the case of address: it calls or addresses someone.',
      compare: 'Finnish has none: it addresses with the nominative (*Mies!*).',
      long: 'In the singular and plural the vocative always has an *s*; in the collective a *z* takes its place. The vocative with an infinitive is a command to someone else: ‘let the boy stop!’',
    },
    related: ['sija', 'imperatiivi'],
    firstIn: 'cases',
  },
  deklinaatio: {
    fi: {
      name: 'taivutusluokka',
      short: '**Taivutusluokka** eli deklinaatio on substantiivien ryhmä, jonka sanat saavat samat päätteet.',
      compare: 'Suomen sanatyypit ovat sama ajatus: *talo*, *kala* ja *vesi* taipuvat kukin omalla tavallaan.',
      long: 'Wikin sanakirja jakaa substantiivit kuuteen taivutusluokkaan. Taivutusluokka on eri asia kuin suku: suku ratkaisee adjektiivin päätteet, taivutusluokka sanan omat päätteet. Taivutusluokan kertoo useimmiten nominatiivin loppu ja sen teemavokaali.',
    },
    en: {
      name: 'declension',
      short: 'A **declension** is a group of nouns whose words take the same endings.',
      compare: 'Finnish word types are the same idea: *talo*, *kala* and *vesi* each inflect their own way.',
      long: 'The wiki dictionary sorts nouns into six declensions. Declension is not gender: gender decides an adjective’s endings, declension the noun’s own. The declension usually shows in the nominative ending and its theme vowel.',
    },
    related: ['suku', 'teemavokaali', 'paate', 'vartalo', 'luokka'],
    firstIn: 'declensions',
  },
  teemavokaali: {
    fi: {
      name: 'teemavokaali',
      short: '**Teemavokaali** on substantiivin vartalon vokaali, joka näkyy nominatiivissa sanan lopussa ja toistuu monissa sen päätteissä.',
      compare: 'Lähin suomessa on vartalon loppuvokaali (*talo-*, *kala-*), mutta valyriassa teemavokaali kertoo, miten sana taipuu.',
      long: 'Esimerkiksi lokatiivi on usein pidentynyt teemavokaali. Oppitunnit käyttävät samaa sanaa myös verbin päätteiden vokaalista (esimerkiksi subjunktiivin *o*), joten katso, puhutaanko substantiivista vai verbistä.',
    },
    en: {
      name: 'theme vowel',
      short: 'The **theme vowel** is the vowel of a noun’s stem that shows at the end of the nominative and comes back in many of its endings.',
      compare: 'The nearest Finnish thing is a stem’s final vowel (*talo-*, *kala-*), but in High Valyrian the theme vowel tells how the word inflects.',
      long: 'The locative, for example, is often the lengthened theme vowel. The lessons use the same word for the vowel of verb endings too (the subjunctive *o*, say), so check whether a noun or a verb is meant.',
    },
    related: ['vartalo', 'deklinaatio', 'lokatiivi'],
    firstIn: 'declensions',
  },
  vartalo: {
    fi: {
      name: 'vartalo',
      short: '**Vartalo** on sanan osa, johon päätteet liitetään.',
      compare: 'Suomessakin vartalo voi vaihdella päätteen edellä: *Lahti* → *Lahde|n*.',
      long: 'Verbeissä vartalon viimeinen äänne ratkaisee päätteet: vokaalivartaloiset ja konsonanttivartaloiset verbit taipuvat eri tavoin. Sanakirjassa vartalo näkyy saumana: vartalo, sitten pääte.',
    },
    en: {
      name: 'stem',
      short: 'A **stem** is the part of a word that endings attach to.',
      compare: 'Finnish stems change before endings too: *Lahti* → *Lahde|n*.',
      long: 'In verbs the stem’s last sound decides the endings: vowel-stem and consonant-stem verbs inflect differently. The dictionary shows the stem with a seam: stem, then ending.',
    },
    related: ['paate', 'juuri', 'vokaalivartalo', 'konsonanttivartalo'],
    firstIn: 'declensions',
  },
  paate: {
    fi: {
      name: 'pääte',
      short: '**Pääte** on vartalon perään tuleva osa, joka kertoo esimerkiksi sijan, luvun tai persoonan.',
      compare: 'Sama ajatus kuin suomen *talo|ssa*, jossa *-ssa* on pääte.',
    },
    en: {
      name: 'ending',
      short: 'An **ending** is the part after the stem that shows, say, the case, the number or the person.',
      compare: 'The same idea as Finnish *talo|ssa*, where *-ssa* is the ending.',
    },
    related: ['vartalo', 'sija', 'persoonapaate'],
    firstIn: 'declensions',
  },
  elollisuus: {
    fi: {
      name: 'elollinen ja eloton',
      short: '**Elollinen** tarkoittaa elävää olentoa, kuten ihmistä tai eläintä, ja **eloton** asiaa tai esinettä.',
      compare: 'Suomen *joku* ~ *jokin* ja *hän* ~ *se* tekevät saman jaon; valyriassa se näkyy myös osoittavissa pronomineissa ja kysymyssanoissa.',
      long: 'Eläimistä puhuja saa usein päättää itse, kuten suomessa lemmikistä voi sanoa *hän*.',
    },
    en: {
      name: 'animate and inanimate',
      short: '**Animate** means a living being, such as a person or an animal, and **inanimate** a thing or an object.',
      compare: 'Finnish *joku* ~ *jokin* and *hän* ~ *se* make the same split; in High Valyrian it also shows in the demonstratives and question words.',
      long: 'For animals the speaker often decides, just as a Finn may call a pet *hän*.',
    },
    related: ['demonstratiivi', 'kysymyssana', 'instrumentaali', 'komitatiivi'],
    firstIn: 'demonstratives',
  },
  ainesana: {
    fi: {
      name: 'ainesana',
      short: '**Ainesana** tarkoittaa ainetta, jota ei lasketa kappaleina, kuten *vesi* tai *olut*; **laskettava** sana tarkoittaa kappaleita, kuten *talo*.',
      compare: 'Jako on sama kuin suomessa: *vettä*, mutta *kaksi taloa*.',
    },
    en: {
      name: 'mass noun',
      short:
        'A **mass noun** means a substance that is not counted in pieces, such as *water* or *beer*; a **count noun** means things that are, such as *house*.',
      compare: 'The split is the same as in Finnish: *vettä*, but *kaksi taloa*.',
    },
    related: ['suku', 'relatiivilause'],
    firstIn: 'genders',
  },
  // Kielioppi upgrade step 2, group 1 (02-nouns.mdx, 01-sounds.mdx): terms the audit found missing.
  'supistuva-sana': {
    fi: {
      name: 'supistuva sana',
      short: '**Supistuva sana** on substantiivi, jonka yksikön nominatiivi on lyhyt ja muissa muodoissa vartalo on pidempi: [[rūs]], genetiivi [[rūho]].',
      compare: 'Suomessakin perusmuoto voi olla vartaloa lyhyempi: *mies*, *miehen*.',
      long: 'Lyhyt muoto on yksikön nominatiivi, akkusatiivi ja vokatiivi; muissa muodoissa vartaloon tulee usein *h*: [[jaos]], [[jaohossa]].',
    },
    en: {
      name: 'contracting noun',
      short: 'A **contracting noun** has a short nominative singular and a longer stem in its other forms: [[rūs]], genitive [[rūho]].',
      compare: 'Finnish has the same idea: *mies*, *miehen*.',
      long: 'The short form is the nominative, accusative and vocative singular; the other forms often add an *h* to the stem: [[jaos]], [[jaohossa]].',
    },
    related: ['vartalo', 'deklinaatio'],
    firstIn: 'declensions',
  },
  lainasana: {
    fi: {
      name: 'lainasana',
      short: '**Lainasana** on toisesta kielestä otettu sana, kuten valyrian [[dothraki]] tai suomen *kahvi*.',
      compare:
        'Suomessakin lainasanat taipuvat omin päättein (*kahvin*); valyriassa lainasana saa jonkin tavallisen taivutusluokan tai kuudennen, johon useimmat niistä kuuluvat.',
      long: 'Vain lainasanoissa on kirjaimia *f*, *kh*, *sh* ja *th*. Lainasana voi säilyttää alkukielensä painon, kuten [[buzdari]].',
    },
    en: {
      name: 'loanword',
      short: 'A **loanword** is a word taken from another language, such as High Valyrian [[dothraki]] or Finnish *kahvi* ‘coffee’.',
      compare:
        'Finnish loanwords take Finnish endings too (*kahvin*); a High Valyrian loanword joins an ordinary declension or the sixth, where most loanwords go.',
      long: 'Only loanwords have the letters *f*, *kh*, *sh* and *th*. A loanword may keep the stress of its source language, like [[buzdari]].',
    },
    related: ['deklinaatio', 'paino'],
    firstIn: 'declensions',
  },
});
